import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Container, Typography, Grid2, Paper, CssBaseline, Box, TextField, ListItem, ListItemIcon, Snackbar, Stack, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';

const Row = ({ data, fetchUpdateUser, fetchRefreshApiKey }) => {

    const handleCopyApiKey = () => {
      navigator.clipboard.writeText(data.api_key || '');
    };

    console.log(data.api_key)
    
    return (
      <Box sx={{ mb: 1, mx: 2 }}>
        <ListItem sx={{ p: 0 }}>
          <Paper elevation={2} sx={{ 
            borderRadius: '8px', 
            p: 2, 
            mx: 1,
            width: '100%'
          }}>
            <Grid2 container alignItems="center" spacing={2}>
              
              <Grid2 item>
                <ListItemIcon>
                  <Avatar variant="rounded" sx={{ bgcolor: '#009999' }}>
                    {data.is_admin ? <SupervisorAccountIcon /> : <PersonIcon />}
                  </Avatar>
                </ListItemIcon>
              </Grid2>

              <Grid2 item xs={1.5}>
                <Typography 
                  variant="body1"
                  sx={{ fontWeight: 500 }}
                  minWidth={75}
                >
                  {data.is_admin ? "Admin" : "User"}
                </Typography>
              </Grid2>

              <Stack direction="column" spacing={2} flexGrow={1}>

                <Stack direction="row" spacing={2}>
                  <Grid2 item xs={2}>
                    <Typography 
                      variant="body1"
                      sx={{ fontWeight: 500 }}
                      minWidth={150}
                    >
                      {data.name}
                    </Typography>
                  </Grid2>

                  <div style={{ flexGrow: 1 }}></div>

                  <Grid2 item xs={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => fetchRefreshApiKey(data.email)}
                        sx={{ 
                          '&:hover': { bgcolor: 'transparent' },
                          color: 'text.secondary'
                        }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                      <KeyIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {data.api_key ? `${data.api_key.slice(0, 4)}...` : 'No API Key'}
                      </Typography>
                      {data.api_key && (
                        <Tooltip title="Copy API Key">
                          <IconButton 
                            size="small"
                            onClick={handleCopyApiKey}
                            sx={{ 
                              '&:hover': { bgcolor: 'transparent' },
                              color: 'text.secondary'
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid2>
              </Stack>


              <Stack direction="row" spacing={2}>
                <Grid2 item xs={3}>
                  <Typography 
                    variant="body1"
                    sx={{ fontWeight: 500 }}
                  >
                    {data.email}
                  </Typography>
                </Grid2>

                <div style={{ flexGrow: 1 }}></div>

                <Grid2 item xs={2} container justifyContent="flex-end" spacing={1}>
                  {!data.is_admin && (
                    <>
                      <Grid2 item>
                        <IconButton 
                          disabled={data.is_active}
                          onClick={() => fetchUpdateUser(data.sub, data.email, !data.is_active)}
                          sx={{ '&:hover': { bgcolor: 'transparent' } }}
                        >
                          <CheckCircleOutlineIcon sx={{ 
                            color: data.is_active ? 'grey' : 'green'
                          }} />
                        </IconButton>
                      </Grid2>
                      <Grid2 item>
                        <IconButton 
                          disabled={!data.is_active}
                          onClick={() => fetchUpdateUser(data.sub, data.email, !data.is_active)}
                          sx={{ '&:hover': { bgcolor: 'transparent' } }}
                        >
                          <NotInterestedIcon sx={{ 
                            color: !data.is_active ? 'grey' : 'red'
                          }} />
                        </IconButton>
                      </Grid2>
                    </>
                  )}
                  {data.is_admin && (
                    <>
                      <Grid2 item>
                        <IconButton disabled sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                          <NotInterestedIcon sx={{ color: 'grey' }} />
                        </IconButton>
                      </Grid2>
                      <Grid2 item>
                        <IconButton disabled sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                          <NotInterestedIcon sx={{ color: 'grey' }} />
                        </IconButton>
                      </Grid2>
                    </>
                  )}
                </Grid2>
              </Stack>
            </Stack>
            </Grid2>
          </Paper>
        </ListItem>
      </Box>
    );
};

const UserAdmin = () => {
  const [userData, setUserData] = useState({});
  const [searchName, setSearchName] = useState("");
  const containerRef = useRef(null);

  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [isloaded, setIsloaded] = useState(false);

  const fetchUserList = async () => {
    try {
      const response = await fetch('/api/user/list');
      const data = await response.json();
      if (data.error){
        window.location.href = '/';
        return;
      }
      Object.entries(data).forEach(([userId, userData]) => {
        setUserData(prevData => ({
          ...prevData,
          [userId]: { ...userData } // 更新 SPS 数据
        }));
      });
    } catch (error) {
      console.error('Error fetching user list:', error);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, [isloaded]);

  const fetchUpdateUser = async (id, email, is_active) => {
    try {
      const response = await fetch('/api/user/enable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, email , is_active}),
      })
      const data = await response.json();
      if (data.success){
        setSeverity("success");
        setSnackbarMessage(data.success);
      }else{
        setSeverity("error");
        setSnackbarMessage(response.error);
      }
      setSnackbarOpen(true);
      fetchUserList();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  }

  const fetchRefreshApiKey = async (email) => {
    try {
      const response = await fetch('/api/user/update_user_api_key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })
      const data = await response.json();
      if (data.success){
        setSeverity("success");
        setSnackbarMessage(data.success);
        setIsloaded(prev => !prev);
      } else {
        setSeverity("error");
        setSnackbarMessage(data.error);
        setIsloaded(prev => !prev);
      }
    } catch (error) {
      console.error('Error refreshing API key:', error);
    }
  };

  const filteredRecords = searchName 
    ? Object.entries(userData)
        .filter(([userId, data]) => (data.email.includes(searchName)))
    : Object.entries(userData); // 按名称排序

  return (
    <Container ref={containerRef}>
      <CssBaseline />
      <Box my={4} mx="auto" display="flex" flexDirection="column" justifyContent="center" alignItems="center" maxWidth={window.innerWidth > 1200 ? "60%" : "100%"}>
        <Typography variant="h4" sx={{ mb: 3 }}>User Admin</Typography>
        
        <TextField
          value={searchName}
          label="Search by Email"
          onChange={(e) => setSearchName(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: searchName && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchName('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack 
          sx={{ 
            width: '100%', 
            maxHeight: '50vh', 
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            }
          }}
        >
          {filteredRecords.map(([id, data]) => (
            <Row
              key={id}
              data={data}
              fetchUpdateUser={fetchUpdateUser}
              fetchRefreshApiKey={fetchRefreshApiKey}
            />
          ))}
        </Stack>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert variant="filled" onClose={() => setSnackbarOpen(false)} severity={severity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserAdmin;